<template>
  <v-card
    data-component-id="ConvertOfferToModelOffer"
  >
    <v-card-title>
      Sélectionner un modèle pour générer l'offre
    </v-card-title>
    <v-progress-linear
      v-if="isLoading"
      indeterminate
      color="primary"
    />
    <v-card-text>
      <v-text-field
        v-model="searchModelOffers"
        outlined
        dense
        hide-details
        clearable
        prepend-inner-icon="mdi-magnify"
        placeholder="Rechercher un modèle d'offre"
        class="mt-2"
      />
      <v-list
        dense
        class="flex-grow-1 overflow-y-auto"
        style="max-height: 400px; min-height: 400px;"
      >
        <template v-if="isLoading">
          <v-skeleton-loader type="list-item@8" />
        </template>

        <template v-else-if="!filteredModelOffers.length">
          <div class="text-center grey--text mt-10">Aucun résultat</div>
        </template>

        <template v-else>
          <v-list-item
            v-for="modelOffer in filteredModelOffers"
            :key="modelOffer.uuid"
            @click="handleSelectModelOffer(modelOffer)"
          >
            <v-list-item-action class="mr-2">
              <v-icon :color="(selectedModelOffers.some(m => m.uuid === modelOffer.uuid)) ? 'success' : 'grey'">mdi-check-circle</v-icon>
            </v-list-item-action>
            <v-list-item-title>{{ modelOffer.name }}</v-list-item-title>
          </v-list-item>
        </template>
      </v-list>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        text
        @click="handleDisagree"
      >
        Annuler
      </v-btn>
      <v-btn
        :disabled="isLoading || !selectedModelOffers.length"
        color="primary"
        text
        @click="handleAccept"
      >
        Accepter
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script setup>
import { ref, computed, onMounted, defineEmits } from 'vue';
import store from '@/store';

import { requests } from '@/mixins/requests';
import { filterListBySearch } from '@/configs/helper';

const emit = defineEmits(['close-dialog', 'handle-loading']);

const isLoading = ref(false);
const modelOffers = ref([]);
const searchModelOffers = ref('');
const selectedModelOffers = ref([]);
const data = ref({});

const handleLoading = value => {
  emit('handle-loading', value);
  isLoading.value = value;
};
const resetDialog = () => {
  data.value = {};
  selectedModelOffers.value = [];
  searchModelOffers.value = '';
  emit('close-dialog');
};

const getModelOffers = async () => {
  handleLoading(true);

  requests.getEntityItems({ key: 'MODEL_OFFERS_LIST' }).then(response => {
    if (!response.success) {
      store.commit('setSnackbar', {
        text: 'Erreur lors de la récupération des modèles d\'offres.',
        color: 'error'
      });
      return;
    }
    modelOffers.value = response.data;
  }).catch(error => {
    store.commit('setSnackbar', {
      text: 'Erreur lors de la récupération des modèles d\'offres.',
      color: 'error'
    });
    console.error('ConvertOfferToModelOffer error', error);
  }).finally(() => {
    handleLoading(false);
  });
};

const convertToOffer = async () => {
  handleLoading(true);
  try {
    for (const selectedModelOffer of selectedModelOffers.value) {
      const { uuid, position, convention, marque, offerFAQs, applicationMethods, tradeUnit, stationCity, ...restData } = selectedModelOffer;
      const response = await requests.createOffer({
        input: {
          ...restData,
          workspace: store.state.centralizedWorkspace,
          position: position?.uuid,
          convention: convention?.uuid,
          marque: marque?.uuid,
          offerFAQs: offerFAQs?.uuid,
          applicationMethods: applicationMethods?.map(method => method?.uuid),
          tradeUnit: tradeUnit?.uuid,
          stationCity: stationCity?.uuid,
          generatedByModel: true
        }
      });
      if (!response.success) {
        store.commit('setSnackbar', {
          text: `Erreur lors de la conversion de l'offre ${selectedModelOffer.name} en modèle d'offre.`,
          color: 'error'
        });
        continue;
      }
    }
    store.commit('setSnackbar', {
      text: 'Offres converties avec succès.',
      color: 'success'
    });
  } catch (error) {
    console.error('ConvertOfferToModelOffer error', error);
    store.commit('setSnackbar', {
      text: 'Erreur lors de la conversion des offres.',
      color: 'error'
    });
  } finally {
    emit('close-dialog');
    handleLoading(false);
    resetDialog();
  }
};

const filteredModelOffers = computed(() => filterListBySearch(modelOffers.value, searchModelOffers.value));

onMounted(() => {
  getModelOffers();
});

const handleSelectModelOffer = modelOffer => {
  const foundedModelOffer = selectedModelOffers.value.find(m => m.uuid === modelOffer.uuid);
  if (foundedModelOffer) {
    selectedModelOffers.value = selectedModelOffers.value.filter(m => m.uuid !== modelOffer.uuid);
  } else {
    selectedModelOffers.value.push(modelOffer);
  }
};

const handleDisagree = () => {
  resetDialog();
};

const handleAccept = async () => {
  convertToOffer();
};
</script>
